import {
  parseOptionSymbolIdentity,
  symbolsEqual,
  toOccSymbol,
  toStreamerSymbol,
} from "@/lib/symbols/symbol-identity";

/**
 * Convert position symbol to DXLink streamer symbol format
 * @param positionSymbol - e.g., "HOOD  270115C00050000"
 * @returns streamer symbol - e.g., ".HOOD270115C50"
 */
export function positionToStreamerSymbol(positionSymbol: string): string {
  return toStreamerSymbol(positionSymbol) ?? positionSymbol;
}

/**
 * Convert DXLink streamer symbol to position format
 * @param streamerSymbol - e.g., ".HOOD270115C50"
 * @returns position symbol - e.g., "HOOD  270115C00050000"
 */
export function streamerToPositionSymbol(streamerSymbol: string): string {
  return toOccSymbol(streamerSymbol) ?? streamerSymbol;
}

export function toPositionSymbol(symbol: string): string {
  return toOccSymbol(symbol) ?? symbol.trim().toUpperCase();
}

/**
 * Extract key components from an option symbol (works with both formats)
 */
export function parseOptionSymbol(symbol: string): {
  underlying: string;
  expiry: string;
  optionType: "C" | "P";
  strike: number;
} | null {
  const identity = parseOptionSymbolIdentity(symbol);
  return identity
    ? {
        underlying: identity.underlying,
        expiry: identity.expiry,
        optionType: identity.optionType,
        strike: identity.strike,
      }
    : null;
}

/**
 * Check if two option symbols refer to the same option
 */
export function isSameOption(symbol1: string, symbol2: string): boolean {
  if (!parseOptionSymbol(symbol1) || !parseOptionSymbol(symbol2)) {
    return false;
  }
  return symbolsEqual(symbol1, symbol2);
}

/**
 * Create a cache key for an option (underlying-expiry-type-strike)
 */
export function getOptionCacheKey(symbol: string): string | null {
  const parsed = parseOptionSymbol(symbol);
  if (!parsed) return null;

  return `${parsed.underlying}-${parsed.expiry}-${parsed.optionType}-${parsed.strike}`;
}

/**
 * Calculate days to expiration (DTE) from an option symbol
 * @param optionSymbol - Option symbol in either position or streamer format
 * @returns DTE as number, or null if unable to parse
 */
export function calculateDTE(optionSymbol: string): number | null {
  const parsed = parseOptionSymbol(optionSymbol);
  if (!parsed) {
    // console.log(`calculateDTE: Failed to parse symbol: ${optionSymbol}`);
    return null;
  }

  // Parse expiry date from YYMMDD format
  const year = 2000 + parseInt(parsed.expiry.substring(0, 2));
  const month = parseInt(parsed.expiry.substring(2, 4)) - 1; // Month is 0-indexed
  const day = parseInt(parsed.expiry.substring(4, 6));

  const expirationDate = new Date(year, month, day);
  const today = new Date();

  // Set both dates to start of day for accurate comparison
  today.setHours(0, 0, 0, 0);
  expirationDate.setHours(0, 0, 0, 0);

  const msPerDay = 24 * 60 * 60 * 1000;
  const daysToExpiry = Math.ceil(
    (expirationDate.getTime() - today.getTime()) / msPerDay,
  );

  // Return 0 for 0DTE (expiring today), not negative for expired
  const dte = Math.max(0, daysToExpiry);
  // console.log(
  //   `calculateDTE: ${optionSymbol} -> Expiry: ${parsed.expiry} (${expirationDate.toDateString()}) -> DTE: ${dte}`,
  // );
  return dte;
}

const SHORT_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/**
 * Format a strike for display: no trailing .0 (450 not 450.0), but keep real
 * decimals (447.5). JS number-to-string already drops trailing zeros.
 */
export function formatStrikeDisplay(strike: number): string {
  return String(strike);
}

/**
 * Format an option symbol tastytrade-style for display:
 *   "AMD   280121C00450000" -> "AMD Jan 21 531d 450 C"
 * (short month + day of expiry, DTE with a "d" suffix recomputed from today,
 * strike without trailing zeros, then C/P). Returns the raw symbol unchanged
 * if it cannot be parsed as an option.
 */
export function formatOptionSymbolDisplay(symbol: string): string {
  const parsed = parseOptionSymbol(symbol);
  if (!parsed) return symbol;

  const month = parseInt(parsed.expiry.substring(2, 4), 10);
  const day = parseInt(parsed.expiry.substring(4, 6), 10);
  const monthName = SHORT_MONTHS[month - 1];
  if (!monthName) return symbol;

  const dte = calculateDTE(symbol);
  const dtePart = dte !== null ? ` ${dte}d` : "";

  return `${parsed.underlying} ${monthName} ${day}${dtePart} ${formatStrikeDisplay(parsed.strike)} ${parsed.optionType}`;
}

/**
 * Get the DTE bucket for a given DTE value
 * Buckets: 0, 7, 14, 45, 60+
 * @param dte - Days to expiration
 * @returns The bucket value (0, 7, 14, 45, or 60)
 */
export function getDTEBucket(dte: number): number {
  if (dte === 0) return 0;
  if (dte <= 7) return 7;
  if (dte <= 14) return 14;
  if (dte <= 45) return 45;
  return 60; // 60+ bucket
}

/**
 * Check if an expiration date is a monthly expiration (third Friday of the month)
 * @param expirationDate - ISO date string or Date object
 * @returns true if it's a monthly expiration
 */
export function isMonthlyExpiration(expirationDate: string | Date): boolean {
  let date: Date;

  if (typeof expirationDate === "string") {
    // Parse the date string properly to avoid timezone issues
    // If it's just a date (YYYY-MM-DD), parse it as local date
    if (expirationDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = expirationDate.split("-").map(Number);
      date = new Date(year, month - 1, day);
    } else {
      // For ISO strings or other formats, use regular parsing
      date = new Date(expirationDate);
    }
  } else {
    date = expirationDate;
  }

  // Monthly options expire on the third Friday of the month
  // Get the day of week (0 = Sunday, 5 = Friday)
  if (date.getDay() !== 5) {
    return false; // Not a Friday
  }

  // Check if it's the third Friday
  const dayOfMonth = date.getDate();

  // The third Friday must be between 15th and 21st
  if (dayOfMonth < 15 || dayOfMonth > 21) {
    return false;
  }

  // Count which Friday of the month this is
  let fridayCount = 0;
  for (let d = 1; d <= dayOfMonth; d++) {
    const checkDate = new Date(date.getFullYear(), date.getMonth(), d);
    if (checkDate.getDay() === 5) {
      fridayCount++;
    }
  }

  return fridayCount === 3;
}
